import { Splide } from '@splidejs/splide';

class SectionBeforeAfterSlider extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;

    // Initialize state with most important options directly in the core config
    this.state = {
      mainCarousel: null,
      mainCarouselOptions: {
        arrows: false, // Primary arrow control
        pagination: false,
        drag: false, // Disable drag on main carousel to prevent interfering with comparison slider
        perPage: 1,
        autoplay: false,
        classes: {
          // Override arrow classes to prevent them from being created
          arrows: 'splide__arrows splide__arrows--disabled',
          arrow: 'splide__arrow splide__arrow--disabled'
        }
      },
      thumbnailCarousel: null,
      thumbnailCarouselOptions: {
        arrows: false, // Primary arrow control
        pagination: false,
        fixedWidth: 94,
        gap: 8,
        rewind: true,
        isNavigation: true, // This allows clicking thumbnails to navigate
        autoplay: false,
        classes: {
          // Override arrow classes to prevent them from being created
          arrows: 'splide__arrows splide__arrows--disabled',
          arrow: 'splide__arrow splide__arrow--disabled'
        },
        breakpoints: {
          768: {
            fixedWidth: 53,
            gap: 8
          }
        }
      }
    };
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    // Initialize DOM references after element is in the DOM
    this.$dom = {
      mainCarousel: this.querySelector('.js-main-carousel'),
      thumbnailCarousel: this.querySelector('.js-thumbnail-carousel'),
      progressBar: this.querySelector('.js-progress-bar')
    };

    // Add a loaded class to enable styling
    this.classList.add('slider-loaded');

    // Use requestAnimationFrame to ensure DOM is fully rendered
    requestAnimationFrame(() => {
      this.initCarousels();
    });
  }

  initCarousels() {
    try {
      // Re-query elements to ensure we have the latest references
      this.$dom = {
        mainCarousel: this.querySelector('.js-main-carousel'),
        thumbnailCarousel: this.querySelector('.js-thumbnail-carousel'),
        progressBar: this.querySelector('.js-progress-bar')
      };

      // Check if elements exist
      if (!this.$dom.mainCarousel || !this.$dom.thumbnailCarousel) {
        console.error('Carousel elements not found in the DOM');
        return;
      }

      // Remove any pre-existing arrow elements
      this.removeArrows();

      if (this.$dom.mainCarousel) this.initMainCarousel();
      if (this.$dom.thumbnailCarousel) this.initThumbnailCarousel();
      this.syncCarousels();

      // After mounting, ensure arrows are removed again (some libraries re-create them)
      setTimeout(() => {
        this.removeArrows();
      }, 100);
    } catch (error) {
      console.error('Error initializing carousels:', error);
    }
  }

  removeArrows() {
    const arrowElements = this.querySelectorAll(
      '.splide__arrows, .splide__arrow, .splide__arrow--prev, .splide__arrow--next'
    );
    arrowElements.forEach(
      (el) => el && el.parentNode && el.parentNode.removeChild(el)
    );
  }

  initMainCarousel() {
    try {
      // First ensure we're using the most restrictive arrow options
      const options = {
        ...this.state.mainCarouselOptions,
        arrows: false,
        arrowPath: '' // Remove the default arrow path
      };

      // Create the Splide instance with these options
      this.state.mainCarousel = new Splide(this.$dom.mainCarousel, options);

      // Directly customize the Components.Arrows to completely disable it
      this.state.mainCarousel.on('ready', () => {
        // If there's an arrows component, disable it
        if (this.state.mainCarousel.Components.Arrows) {
          this.state.mainCarousel.Components.Arrows.arrows = {
            prev: null,
            next: null
          };
          // Prevent the arrows from being mounted
          this.state.mainCarousel.Components.Arrows.mount = function () {};
        }
        // Remove any arrows that might have been created
        this.removeArrows();
      });

      // Update progress bar
      this.state.mainCarousel.on('mounted move', () => {
        const total =
          this.state.mainCarousel.Components.Controller.getEnd() + 1;
        const width = `${100 / total}%`;
        const left = `${(100 * this.state.mainCarousel.index) / total}%`;

        if (this.$dom.progressBar) {
          this.$dom.progressBar.style.width = width;
          this.$dom.progressBar.style.left = left;
        }
      });

      // Mount with arrows disabled
      this.state.mainCarousel.mount();

      // One more arrow removal after mounting
      this.removeArrows();
    } catch (error) {
      console.error('Error initializing main carousel:', error);
    }
  }

  initThumbnailCarousel() {
    try {
      // Use the same approach as the main carousel for consistency
      const options = {
        ...this.state.thumbnailCarouselOptions,
        arrows: false,
        arrowPath: '' // Remove the default arrow path
      };

      // Create the thumbnail carousel
      this.state.thumbnailCarousel = new Splide(
        this.$dom.thumbnailCarousel,
        options
      );

      // Directly customize the Components.Arrows to completely disable it
      this.state.thumbnailCarousel.on('ready', () => {
        // If there's an arrows component, disable it
        if (this.state.thumbnailCarousel.Components.Arrows) {
          this.state.thumbnailCarousel.Components.Arrows.arrows = {
            prev: null,
            next: null
          };
          // Prevent the arrows from being mounted
          this.state.thumbnailCarousel.Components.Arrows.mount = function () {};
        }
        // Remove any arrows that might have been created
        this.removeArrows();
      });

      // Mount with arrows disabled
      this.state.thumbnailCarousel.mount();

      // One more arrow removal for safety
      this.removeArrows();
    } catch (error) {
      console.error('Error initializing thumbnail carousel:', error);
    }
  }

  syncCarousels() {
    try {
      if (this.state.mainCarousel && this.state.thumbnailCarousel) {
        this.state.mainCarousel.sync(this.state.thumbnailCarousel);

        // Apply opacity to active thumbnails
        this.state.thumbnailCarousel.on('active', (Slide) => {
          const slides = this.querySelectorAll(
            '.js-thumbnail-carousel .splide__slide'
          );
          slides.forEach((slide) => {
            slide.classList.remove('opacity-50');
          });

          if (Slide.slide) {
            Slide.slide.classList.add('opacity-50');
          }
        });

        // Final check for arrows after sync
        setTimeout(() => {
          this.removeArrows();
        }, 50);
      }
    } catch (error) {
      console.error('Error syncing carousels:', error);
    }
  }
}

// Single registration block - this is the only one we need
if (!window.customElements.get('section-before-after-slider')) {
  window.customElements.define(
    'section-before-after-slider',
    SectionBeforeAfterSlider
  );
}
